import { useEffect, useState } from 'react'
import { useAuditStore } from '../store'
import { useAuthStore } from '../../auth/store'
import { usePetsStore } from '../../pets/store'
import { Card, CardContent } from '../../../shared/components/Card'
import { EmptyState } from '../../../shared/components/EmptyState'
import { ResponsiveLayout } from '../../../shared/components/layout'
import { StatusCard, Badge } from '../../../shared/components/widgets'
import { formatDistanceToNow } from '../../../shared/lib/date-utils'
import type { AuditEntityType, AuditAction, CareAuditLog } from '@rondela/shared'
import { Navigate } from 'react-router-dom'

// Field name translations for display
const fieldLabels: Record<string, string> = {
  nombre: 'Nombre',
  dosis: 'Dosis',
  frecuencia: 'Frecuencia',
  fechaInicio: 'Fecha inicio',
  fechaFin: 'Fecha fin',
  instrucciones: 'Instrucciones',
  horaPreferida: 'Hora preferida',
  hora: 'Hora',
  tipo: 'Tipo',
  fecha: 'Fecha',
  veterinario: 'Veterinario',
  clinica: 'Clínica',
  motivo: 'Motivo',
  diagnostico: 'Diagnóstico',
  tratamiento: 'Tratamiento',
  notas: 'Notas',
  proximaCita: 'Próxima cita',
  fechaAplicacion: 'Fecha aplicación',
  fechaProxima: 'Fecha próxima',
  lote: 'Lote',
  especie: 'Especie',
  raza: 'Raza',
  peso: 'Peso',
  fechaNacimiento: 'Fecha nacimiento',
  condicionesMedicas: 'Condiciones médicas',
  status: 'Estado',
  activa: 'Activa',
  completada: 'Completada',
}

const entityTypeLabels: Record<AuditEntityType, string> = {
  pet: 'Mascota',
  medication: 'Medicamento',
  checkup: 'Control',
  vaccine: 'Vacuna',
  clinical_event: 'Evento Clínico',
  symptom: 'Síntoma',
  note: 'Nota',
  weight: 'Peso',
}

const actionLabels: Record<AuditAction, string> = {
  created: 'Creó',
  updated: 'Actualizó',
  deleted: 'Eliminó',
  status_changed: 'Cambió estado de',
  dose_taken: 'Administró dosis de',
  dose_skipped: 'Omitió dosis de',
  completed: 'Completó',
  discontinued: 'Discontinuó',
  restored: 'Restauró',
}

const entityTypeIcons: Record<AuditEntityType, string> = {
  pet: '🐾',
  medication: '💊',
  checkup: '🩺',
  vaccine: '💉',
  clinical_event: '📋',
  symptom: '🤒',
  note: '📝',
  weight: '⚖️',
}

export function BitacoraPage() {
  const { user } = useAuthStore()
  const {
    auditLogs,
    pagination,
    filters,
    isLoading,
    error,
    fetchAuditLogs,
    setFilters,
    clearFilters,
    clearError,
  } = useAuditStore()
  const { pets, fetchPets } = usePetsStore()

  const [filterPetId, setFilterPetId] = useState<string>('')
  const [filterEntityType, setFilterEntityType] = useState<string>('')
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())

  const toggleExpand = (id: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '(vacío)'
    if (typeof value === 'boolean') return value ? 'Sí' : 'No'
    if (typeof value === 'string' && value === '') return '(vacío)'
    return String(value)
  }

  const hasChanges = (log: CareAuditLog): boolean => {
    return !!(log.changes && Object.keys(log.changes).length > 0)
  }

  // Only admins can access this page
  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />
  }

  useEffect(() => {
    fetchAuditLogs()
    fetchPets()
  }, [fetchAuditLogs, fetchPets])

  const handleApplyFilters = () => {
    const newFilters = {
      petId: filterPetId || undefined,
      entityType: (filterEntityType as AuditEntityType) || undefined,
    }
    setFilters(newFilters)
    fetchAuditLogs(1, newFilters)
  }

  const handleClearFilters = () => {
    setFilterPetId('')
    setFilterEntityType('')
    clearFilters()
    fetchAuditLogs(1, {})
  }

  const handlePageChange = (page: number) => {
    fetchAuditLogs(page)
  }

  const hasActiveFilters = filters.petId || filters.entityType || filters.userId

  return (
    <ResponsiveLayout>
      <div className="page bitacora-page">
        <div className="page-header">
          <h1>Bitácora de Cuidado</h1>
          <p className="page-subtitle">Registro de todas las acciones realizadas</p>
        </div>

      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={clearError} className="alert-dismiss">&times;</button>
        </div>
      )}

      {/* Filters */}
      <Card className="filters-card">
        <CardContent>
          <div className="filters-row">
            <div className="form-group">
              <label htmlFor="filterPet">Mascota</label>
              <select
                id="filterPet"
                value={filterPetId}
                onChange={(e) => setFilterPetId(e.target.value)}
                className="form-control"
              >
                <option value="">Todas</option>
                {pets.map((pet) => (
                  <option key={pet.id} value={pet.id}>
                    {pet.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="filterType">Tipo</label>
              <select
                id="filterType"
                value={filterEntityType}
                onChange={(e) => setFilterEntityType(e.target.value)}
                className="form-control"
              >
                <option value="">Todos</option>
                {Object.entries(entityTypeLabels).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group filter-actions">
              <button onClick={handleApplyFilters} className="btn btn-primary">
                Filtrar
              </button>
              {hasActiveFilters && (
                <button onClick={handleClearFilters} className="btn btn-ghost">
                  Limpiar
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {isLoading && auditLogs.length === 0 ? (
        <div className="loading-container">
          <div className="spinner" />
        </div>
      ) : auditLogs.length === 0 ? (
        <EmptyState
          title="Sin registros"
          description={hasActiveFilters
            ? 'No hay registros que coincidan con los filtros.'
            : 'Aún no hay acciones registradas en la bitácora.'
          }
        />
      ) : (
        <>
          <div className="audit-list">
            {auditLogs.map((log) => {
              const isExpanded = expandedItems.has(log.id)
              const showExpandButton = hasChanges(log)
              const actionStatus = log.action === 'deleted' ? 'error' :
                log.action === 'dose_skipped' || log.action === 'discontinued' ? 'warning' :
                log.action === 'created' || log.action === 'dose_taken' || log.action === 'completed' ? 'success' : 'info'

              return (
                <StatusCard
                  key={log.id}
                  status={actionStatus}
                  stripe
                  expandable={showExpandButton}
                  expanded={isExpanded}
                  onExpandedChange={() => toggleExpand(log.id)}
                  className="audit-card"
                >
                  <StatusCard.Header>
                    <span className="audit-card__icon">
                      {entityTypeIcons[log.entityType]}
                    </span>
                    <div className="audit-card__info">
                      <StatusCard.Title>
                        <Badge variant="status" status={actionStatus === 'error' ? 'error' : actionStatus === 'warning' ? 'warning' : actionStatus === 'success' ? 'success' : 'info'}>
                          {actionLabels[log.action]}
                        </Badge>
                        <span className="audit-card__entity-type">{entityTypeLabels[log.entityType]}</span>
                        {log.entityName && <span className="audit-card__entity-name">"{log.entityName}"</span>}
                      </StatusCard.Title>
                      <StatusCard.Subtitle>
                        {log.petNombre && <span className="audit-card__pet">🐾 {log.petNombre}</span>}
                        {log.actionDetail && <span className="audit-card__detail">{log.actionDetail}</span>}
                      </StatusCard.Subtitle>
                    </div>
                    <div className="audit-card__meta">
                      <span className="audit-card__user">{log.userNombre}</span>
                      <Badge variant="time">{formatDistanceToNow(log.createdAt)}</Badge>
                    </div>
                  </StatusCard.Header>
                  {showExpandButton && (
                    <StatusCard.Content>
                      <div className="audit-changes">
                        <div className="audit-changes-title">Cambios realizados:</div>
                        {log.changes && Object.entries(log.changes).map(([field, change]) => (
                          <div key={field} className="audit-change-item">
                            <span className="audit-change-field">
                              {fieldLabels[field] || field}:
                            </span>
                            <span className="audit-change-from">
                              {formatValue(change.from)}
                            </span>
                            <span className="audit-change-arrow">→</span>
                            <span className="audit-change-to">
                              {formatValue(change.to)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </StatusCard.Content>
                  )}
                </StatusCard>
              )
            })}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="btn btn-ghost"
              >
                Anterior
              </button>
              <span className="pagination-info">
                Página {pagination.page} de {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="btn btn-ghost"
              >
                Siguiente
              </button>
            </div>
          )}

          <p className="results-count">
            {pagination.total} registro{pagination.total !== 1 ? 's' : ''} en total
          </p>
        </>
      )}
      </div>
    </ResponsiveLayout>
  )
}
